import {DATASET_MIXPANEL, ResourceType} from 'common/report/constants';
import {SpecialTimeUnit, TimeUnit} from 'common/widgets/date-range-picker/models/types';

import {InsightsResourceType} from '../util/types';

export const MINUTE_MS = 1000 * 60;
export const FEATURE_GATES_UNLIMITED = 9223372036854776000; // python max int, aka featureGates.unlimited

export const NEW_QB_CHANGE_NOTICE = {
  'confirm-button-label': `Let's do it!`,
  title: `Switch to new version of Insights?`,
  description: `
    The report you’re opening was created with a newer version of Insights. You’ll need to opt-in to the beta to view this report.
  `,
};

export const PERSISTED_STATE = {
  NEW_QB: `insights_new_qb_state`,
};

export const TOP_EVENTS = `_topEvents`;
export const TOP = {
  EVENTS: {
    PROPERTIES: `_topEventsProperties`,
    PROPERTIES_BY_EVENT: `_topEventsPropertiesByEvent`,
  },
  PEOPLE: {
    PROPERTIES: `_topPeopleProperties`,
    PROPERTIES_BY_PROFILE_TYPE: `_topPeoplePropertiesByProfileType`,
  },
};
export const RECENT = {
  EVENTS: `_recentEvents`,
  PROPERTIES: `_recentProperties`,
  PEOPLE: `_recentPeople`,
};

export const SCREENS = {
  CONTEXTUAL: `builder-screen-contextual`,
  SHOW: {
    ALL: `builder-screen-show-all`,
    COHORTS: `builder-screen-show-cohorts`,
    DATASETS: `builder-screen-show-datasets`,
    EVENTS: `builder-screen-show-events`,
    EVENT_PROPERTIES: `builder-screen-show-event-properties`,
    PEOPLE_PROPERTIES: `builder-screen-show-people-properties`,
    TAGGED_EVENTS: `builder-screen-tagged-events`,
  },
  GROUP: {
    ALL: `builder-screen-group-all`,
    PROPERTY_TYPECAST: `builder-screen-group-property-typecast`,
    PROPERTY_TIMEUNIT: `builder-screen-group-property-timeunit`,
    COHORTS: `builder-screen-group-cohorts`,
  },
  FILTER: {
    CONTEXTUAL: `builder-screen-filter-contextual`,
    PROPERTIES: `builder-screen-filter-properties`,
    PROPERTY_VALUES: `builder-screen-filter-property-values`,
    COHORTS: `builder-screen-filter-cohorts`,
  },
  TIME: {
    OPTIONS: `builder-screen-time`,
    DATE_RANGE_PICKER: `builder-screen-timepicker`,
  },
  TIME_COMPARISON: {
    OPTION: `timeComparison`,
    DATE_RANGE_PICKER: `timeComparisonCustom`,
  },
};

export const SHOW_LIMIT = 10;

export {DATASET_MIXPANEL};

export const DATASETS = {
  [DATASET_MIXPANEL]: {
    datasetId: DATASET_MIXPANEL,
    displayName: `Mixpanel`,
    pillLabel: `MP`,
    description: `Product usage & user data`,
  },
};

export const FEATURE_FLAGS = {
  B2B_ANALYTICS: `b2b_analytics`,
  BEHAVIORS_OVER_TIME: `insights_behaviors_over_time`,
  COHORTS: `cohorts`,
  CUSTOM_PROPERTY: `custom_property`,
  DATA_MANAGEMENT: `data_management`,
  DEFAULT_QUERY_SAMPLING: `qts_by_default`,
  INLINE_COHORTS: `inline_cohorts`,
  NEW_METRICS_MODE: `insights_new_metrics_mode`,
  OMNISEARCH: `omnisearch`,
  QUERY_SAMPLING: `query_time_sampling`,
  // [shawn] Sessions flag is hardcoded in query-api as
  // funnels_sessions, needs to be cleaned up once
  // the blocker is resolved
  SESSIONS: `funnels_sessions`,
  SHOW_LAST_1_DAY: `show_last_1_day`,
  SHOW_LAST_7_DAYS: `show_last_7_days`,
  SHOW_UBER_WELCOME: `show_uber_welcome`,
  SHOW_UBER_WELCOME_PROD: `show_uber_welcome_prod`,
  NEW_DATE_PICKER: `new_date_picker`,
};

export const FEATURE_GATES = {
  B2B_ANALYTICS: `b2b_analytics`,
};

export const TIME_COMPARISON_DISABLED_REASONS = {
  PEOPLE_QUERY: `people_query`,
  STACKED_CHART: `stacked_chart`,
  PIE_CHART: `pie_chart`,
  TOP_PATHS: `top_paths`,
  RETENTION_CURVE: `retention_curve`,
};

export const GENERIC_ERROR_MESSAGE = `Something went wrong`;

export const CONTEXTUAL_MENU_LABELS = {
  COMPARE_COHORTS: `Compare to cohorts`,
  COMPARE_EVENTS: `Compare to events`,
  COMPARE_PROPERTIES: `Compare to properties`,
  BREAKDOWN: `Breakdown`,
  FILTER_COHORT: `Filter by cohort`,
  FILTER_PROPERTY: `Filter by property`,
};

export const ROLLING_WINDOW_SIZE_OPTIONS = {
  minute: [15, 30, 60],
  hour: [6, 12, 24],
  day: [7, 14, 28],
  week: [2, 4, 5, 8],
  month: [3, 6],
  quarter: [2, 3, 4],
};

export const TIME_UNITS = {
  MINUTE: TimeUnit.Minute,
  HOUR: TimeUnit.Hour,
  DAY: TimeUnit.Day,
  WEEK: TimeUnit.Week,
  MONTH: TimeUnit.Month,
  QUARTER: TimeUnit.Quarter,
  YEAR: TimeUnit.Year,
};

export const TIME_UNIT_QUERY_LIST = [
  TIME_UNITS.HOUR,
  TIME_UNITS.DAY,
  TIME_UNITS.WEEK,
  TIME_UNITS.MONTH,
  TIME_UNITS.QUARTER,
  TIME_UNITS.YEAR,
];

export const TIME_UNIT_GROUPING_LIST = [...TIME_UNIT_QUERY_LIST, SpecialTimeUnit.HourOfDay, SpecialTimeUnit.DayOfWeek];

export const START_OF_YESTERDAY = `$start_of_previous_day`;
export const END_OF_YESTERDAY = `$end_of_previous_day`;
export const START_OF_TODAY = `$start_of_current_day`;
export const END_OF_TODAY = `$end_of_current_day`;

export const QUERY_BUILDER_RESOURCE_TYPE_MAPPING: Record<ResourceType, InsightsResourceType> = {
  [ResourceType.Cohort]: InsightsResourceType.Cohorts,
  [ResourceType.Event]: InsightsResourceType.Events,
  [ResourceType.Formula]: InsightsResourceType.Formulas,
  [ResourceType.User]: InsightsResourceType.People,
  [ResourceType.Other]: InsightsResourceType.Other,
};

export const INSIGHTS_GROUP_PROPERTIES = [
  `customProperty`,
  `customPropertyId`,
  `value`,
  `propertyType`,
  `resourceType`,
  `typeCast`,
  `unit`,
  `dataGroupId`,
  `tempDataGroupId`,
  `propertyObjectKey`,
  `behavior`,
];
